import { Wrapper } from '@googlemaps/react-wrapper';
import { Card, Col, Tabs, Tooltip } from 'antd';
import { VFC } from 'react';
import { isNil } from 'rambda';
import { FormattedMessage, useIntl } from 'react-intl';
import { InformationTab } from './Information';
import { MapTab } from './Map';
import { DecoderTab } from './Decoder';
import { GOOGLE_MAPS_API_KEY } from '../../../consts/common';
import { Floorplan } from '../../../components/Floorplan/Floorplan';
import { useMeterContext } from '../../../contexts/Meter';
import { MeterDetailContent as messages } from '../messages';

const { TabPane } = Tabs;

export const MeterDetailContent: VFC = () => {
  const intl = useIntl();
  const { enmonMeter, inventoryMeter, isLoading } = useMeterContext();

  const { inventory_location_building: meterBuilding, inventory_location: meterLocation } = inventoryMeter ?? {};

  const { buildingFloor, location, floorPlanLocation, registers = [] } = enmonMeter ?? {};

  const [meterLng, meterLat] = location?.coordinates ?? [null, null];
  const [meterFloorplanLng, meterFloorplanLat] = floorPlanLocation?.coordinates ?? [null, null];

  const buildingId = meterBuilding?.id;
  const locationId = meterLocation?.id;

  const hasFloorplanValues =
    !isNil(meterFloorplanLat) &&
    !isNil(meterFloorplanLng) &&
    !isNil(buildingFloor) &&
    (!isNil(buildingId) || !isNil(locationId));

  const hasMapValues = !isNil(meterLat) && !isNil(meterLng);

  const dataSourceId = registers?.[0]?.dataSourceId;
  const hasRegisterDataSource = !!dataSourceId;

  return (
    <Card bodyStyle={{ padding: 0 }} bordered={false} loading={isLoading}>
      <Col>
        <Tabs style={{ height: '100%' }} type="card">
          <TabPane key="information" tab={<FormattedMessage {...messages.information} />}>
            <InformationTab enmonMeter={enmonMeter} intl={intl} inventoryMeter={inventoryMeter} />
          </TabPane>

          <TabPane
            key="map"
            disabled={!hasMapValues}
            tab={
              !hasMapValues ? (
                <Tooltip title={<FormattedMessage {...messages.coordinatesNotAvailable} />}>
                  <FormattedMessage {...messages.map} />
                </Tooltip>
              ) : (
                <FormattedMessage {...messages.map} />
              )
            }
          >
            <Wrapper apiKey={GOOGLE_MAPS_API_KEY}>
              <MapTab meterLat={meterLat} meterLng={meterLng} />
            </Wrapper>
          </TabPane>

          <TabPane
            key="floorplan"
            disabled={!hasFloorplanValues}
            tab={
              !hasFloorplanValues ? (
                <Tooltip title={<FormattedMessage {...messages.floorplanNotAvailable} />}>
                  <FormattedMessage {...messages.floorplan} />
                </Tooltip>
              ) : (
                <FormattedMessage {...messages.floorplan} />
              )
            }
          >
            <Wrapper apiKey={GOOGLE_MAPS_API_KEY}>
              {!!hasFloorplanValues && (
                <Floorplan
                  buildingFloor={buildingFloor}
                  buildingId={buildingId}
                  locationId={buildingId ? undefined : locationId}
                  markers={[{ id: 'default', lat: meterFloorplanLat, lng: meterFloorplanLng }]}
                />
              )}
            </Wrapper>
          </TabPane>

          <TabPane
            key="messages"
            disabled={!hasRegisterDataSource}
            tab={
              !hasRegisterDataSource ? (
                <Tooltip title={<FormattedMessage {...messages.noDataSource} />}>
                  <FormattedMessage {...messages.data} />
                </Tooltip>
              ) : (
                <FormattedMessage {...messages.data} />
              )
            }
          >
            <DecoderTab dataSourceId={dataSourceId} />
          </TabPane>
        </Tabs>
      </Col>
    </Card>
  );
};
